import React, { useState, useEffect, useMemo } from 'react';
import { Hash, FileText, Copy, Download, Shuffle, BarChart3, Eye, Calculator } from 'lucide-react';

interface CountMethod {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  fn: (str: string) => number;
  color: string;
}

interface DetailedStats {
  characters: number;
  charactersNoSpaces: number;
  words: number;
  sentences: number;
  paragraphs: number;
  lines: number;
  letters: number;
  digits: number;
  punctuation: number;
  whitespace: number;
  uppercase: number;
  lowercase: number;
  uniqueChars: number;
  vowels: number;
  consonants: number;
}

const StringLengthCounter: React.FC = () => {
  const [input, setInput] = useState('');
  const [selectedMethods, setSelectedMethods] = useState<string[]>(['characters', 'words', 'sentences']);
  const [copySuccess, setCopySuccess] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  const countMethods: CountMethod[] = [
    {
      id: 'characters',
      name: 'Characters',
      description: 'Total character count including spaces',
      icon: <Hash className="w-4 h-4" />,
      fn: (str) => str.length,
      color: 'blue'
    },
    {
      id: 'charactersNoSpaces',
      name: 'Characters (No Spaces)',
      description: 'Character count excluding whitespace',
      icon: <FileText className="w-4 h-4" />,
      fn: (str) => str.replace(/\s/g, '').length,
      color: 'green'
    },
    {
      id: 'words',
      name: 'Words',
      description: 'Number of words separated by spaces',
      icon: <FileText className="w-4 h-4" />,
      fn: (str) => str.trim() ? str.trim().split(/\s+/).length : 0,
      color: 'purple'
    },
    {
      id: 'sentences',
      name: 'Sentences',
      description: 'Number of sentences (ending with . ! ?)',
      icon: <FileText className="w-4 h-4" />,
      fn: (str) => (str.match(/[.!?]+/g) || []).length,
      color: 'red'
    },
    {
      id: 'paragraphs',
      name: 'Paragraphs',
      description: 'Number of paragraphs (separated by double newlines)',
      icon: <FileText className="w-4 h-4" />,
      fn: (str) => str.trim() ? str.split(/\n\s*\n/).filter(p => p.trim()).length : 0,
      color: 'yellow'
    },
    {
      id: 'lines',
      name: 'Lines',
      description: 'Number of lines (separated by newlines)',
      icon: <FileText className="w-4 h-4" />,
      fn: (str) => str ? str.split('\n').length : 0,
      color: 'indigo'
    },
    {
      id: 'letters',
      name: 'Letters Only',
      description: 'Alphabetic characters only',
      icon: <FileText className="w-4 h-4" />,
      fn: (str) => (str.match(/[a-zA-Z]/g) || []).length,
      color: 'pink'
    },
    {
      id: 'digits',
      name: 'Digits',
      description: 'Numeric characters (0-9)',
      icon: <Hash className="w-4 h-4" />,
      fn: (str) => (str.match(/\d/g) || []).length,
      color: 'orange'
    },
    {
      id: 'punctuation',
      name: 'Punctuation',
      description: 'Punctuation marks and symbols',
      icon: <FileText className="w-4 h-4" />,
      fn: (str) => (str.match(/[^\w\s]/g) || []).length,
      color: 'teal'
    },
    {
      id: 'uniqueChars',
      name: 'Unique Characters',
      description: 'Number of distinct characters',
      icon: <Eye className="w-4 h-4" />,
      fn: (str) => new Set(str).size,
      color: 'cyan'
    },
    {
      id: 'vowels',
      name: 'Vowels',
      description: 'Vowel characters (a, e, i, o, u)',
      icon: <FileText className="w-4 h-4" />,
      fn: (str) => (str.match(/[aeiouAEIOU]/g) || []).length,
      color: 'emerald'
    },
    {
      id: 'consonants',
      name: 'Consonants',
      description: 'Consonant characters',
      icon: <FileText className="w-4 h-4" />,
      fn: (str) => (str.match(/[bcdfghjklmnpqrstvwxyzBCDFGHJKLMNPQRSTVWXYZ]/g) || []).length,
      color: 'violet'
    }
  ];

  const detailedStats: DetailedStats = useMemo(() => {
    return {
      characters: input.length,
      charactersNoSpaces: input.replace(/\s/g, '').length,
      words: input.trim() ? input.trim().split(/\s+/).length : 0,
      sentences: (input.match(/[.!?]+/g) || []).length,
      paragraphs: input.trim() ? input.split(/\n\s*\n/).filter(p => p.trim()).length : 0,
      lines: input ? input.split('\n').length : 0,
      letters: (input.match(/[a-zA-Z]/g) || []).length,
      digits: (input.match(/\d/g) || []).length,
      punctuation: (input.match(/[^\w\s]/g) || []).length,
      whitespace: (input.match(/\s/g) || []).length,
      uppercase: (input.match(/[A-Z]/g) || []).length,
      lowercase: (input.match(/[a-z]/g) || []).length,
      uniqueChars: new Set(input).size,
      vowels: (input.match(/[aeiouAEIOU]/g) || []).length,
      consonants: (input.match(/[bcdfghjklmnpqrstvwxyzBCDFGHJKLMNPQRSTVWXYZ]/g) || []).length
    };
  }, [input]);

  const toggleMethod = (methodId: string) => {
    setSelectedMethods(prev => 
      prev.includes(methodId) 
        ? prev.filter(id => id !== methodId)
        : [...prev, methodId]
    );
  };

  const copyResults = async () => {
    const results = selectedMethods.map(id => {
      const method = countMethods.find(m => m.id === id);
      return `${method?.name}: ${method?.fn(input)}`;
    }).join('\n');
    
    await navigator.clipboard.writeText(results);
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
  };

  const downloadResults = () => {
    const results = selectedMethods.map(id => {
      const method = countMethods.find(m => m.id === id);
      return `${method?.name}: ${method?.fn(input)}`;
    }).join('\n');
    
    const blob = new Blob([`Text Analysis Results\n\nInput Text:\n${input}\n\nResults:\n${results}`], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'text-analysis.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const generateSampleText = () => {
    const samples = [
      "Hello World! This is a sample text with multiple sentences. How are you today? I hope you're doing well.\n\nThis is a new paragraph with some numbers like 123 and 456. It also contains various punctuation marks: @#$%^&*().",
      "The quick brown fox jumps over the lazy dog. This sentence contains every letter of the alphabet!\n\n123 Main Street\nAnytown, USA 12345\n\nPhone: (555) 123-4567\nEmail: test@example.com",
      "Programming is fun! React, TypeScript, and Tailwind CSS make development enjoyable.\n\nFeatures:\n- Component-based architecture\n- Type safety\n- Utility-first CSS\n\nVersion 2.0 released in 2024.",
      "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.\n\nUt enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat."
    ];
    setInput(samples[Math.floor(Math.random() * samples.length)]);
  };

  const getColorClasses = (color: string) => {
    const colors = {
      blue: 'bg-blue-50 border-blue-200 text-blue-800',
      green: 'bg-green-50 border-green-200 text-green-800',
      purple: 'bg-purple-50 border-purple-200 text-purple-800',
      red: 'bg-red-50 border-red-200 text-red-800',
      yellow: 'bg-yellow-50 border-yellow-200 text-yellow-800',
      indigo: 'bg-indigo-50 border-indigo-200 text-indigo-800',
      pink: 'bg-pink-50 border-pink-200 text-pink-800',
      orange: 'bg-orange-50 border-orange-200 text-orange-800',
      teal: 'bg-teal-50 border-teal-200 text-teal-800',
      cyan: 'bg-cyan-50 border-cyan-200 text-cyan-800',
      emerald: 'bg-emerald-50 border-emerald-200 text-emerald-800',
      violet: 'bg-violet-50 border-violet-200 text-violet-800'
    };
    return colors[color as keyof typeof colors] || colors.blue;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 p-4">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-800 mb-2 flex items-center justify-center gap-3">
            <Calculator className="text-blue-600" />
            Advanced String Length Counter
          </h1>
          <p className="text-gray-600">Comprehensive text analysis with multiple counting methods</p>
        </div>

        {/* Input Section */}
        <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <label className="text-lg font-semibold text-gray-700">Input Text</label>
            <div className="flex gap-2">
              <button
                onClick={generateSampleText}
                className="flex items-center gap-2 px-3 py-1 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors text-sm"
              >
                <Shuffle size={16} />
                Sample Text
              </button>
              <button
                onClick={() => setShowDetails(!showDetails)}
                className="flex items-center gap-2 px-3 py-1 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors text-sm"
              >
                <BarChart3 size={16} />
                {showDetails ? 'Hide' : 'Show'} Details
              </button>
            </div>
          </div>
          
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Enter your text here for analysis..."
            className="w-full h-40 p-4 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none resize-none text-gray-700 transition-colors"
          />
        </div>

        {/* Count Methods Selection */}
        <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <Hash className="text-blue-600" />
              <h2 className="text-xl font-semibold text-gray-800">Select Count Methods</h2>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setSelectedMethods(countMethods.map(m => m.id))}
                className="px-3 py-1 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors text-sm"
              >
                Select All
              </button>
              <button
                onClick={() => setSelectedMethods([])}
                className="px-3 py-1 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors text-sm"
              >
                Clear All
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {countMethods.map((method) => (
              <div
                key={method.id}
                onClick={() => toggleMethod(method.id)}
                className={`p-4 rounded-xl border-2 cursor-pointer transition-all duration-300 hover:scale-105 hover:shadow-lg ${
                  selectedMethods.includes(method.id)
                    ? `border-${method.color}-500 ${getColorClasses(method.color)} shadow-md`
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    {method.icon}
                    <h3 className={`font-semibold ${
                      selectedMethods.includes(method.id) ? '' : 'text-gray-800'
                    }`}>
                      {method.name}
                    </h3>
                  </div>
                  {selectedMethods.includes(method.id) && (
                    <div className={`w-3 h-3 bg-${method.color}-500 rounded-full`}></div>
                  )}
                </div>
                <p className={`text-sm ${
                  selectedMethods.includes(method.id) ? '' : 'text-gray-600'
                }`}>
                  {method.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Results Section */}
        {selectedMethods.length > 0 && (
          <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-800">Results</h2>
              <div className="flex gap-2">
                <button
                  onClick={copyResults}
                  disabled={!input}
                  className="flex items-center gap-2 px-3 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors text-sm disabled:opacity-50"
                >
                  <Copy size={16} />
                  {copySuccess ? 'Copied!' : 'Copy Results'}
                </button>
                <button
                  onClick={downloadResults}
                  disabled={!input}
                  className="flex items-center gap-2 px-3 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors text-sm disabled:opacity-50"
                >
                  <Download size={16} />
                  Download
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {selectedMethods.map((methodId) => {
                const method = countMethods.find(m => m.id === methodId);
                if (!method) return null;
                
                const count = method.fn(input);
                return (
                  <div
                    key={methodId}
                    className={`p-4 rounded-xl border-2 ${getColorClasses(method.color)}`}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      {method.icon}
                      <h3 className="font-semibold">{method.name}</h3>
                    </div>
                    <div className="text-2xl font-bold mb-1">{count.toLocaleString()}</div>
                    <p className="text-sm opacity-80">{method.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Detailed Statistics */}
        {showDetails && input && (
          <div className="bg-white rounded-2xl shadow-xl p-6">
            <div className="flex items-center gap-3 mb-6">
              <BarChart3 className="text-purple-600" />
              <h2 className="text-xl font-semibold text-gray-800">Detailed Analysis</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-700 border-b pb-2">Basic Counts</h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Total Characters:</span>
                    <span className="font-semibold">{detailedStats.characters.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Without Spaces:</span>
                    <span className="font-semibold">{detailedStats.charactersNoSpaces.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Words:</span>
                    <span className="font-semibold">{detailedStats.words.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Sentences:</span>
                    <span className="font-semibold">{detailedStats.sentences.toLocaleString()}</span>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="font-semibold text-gray-700 border-b pb-2">Character Types</h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Letters:</span>
                    <span className="font-semibold">{detailedStats.letters.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Digits:</span>
                    <span className="font-semibold">{detailedStats.digits.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Punctuation:</span>
                    <span className="font-semibold">{detailedStats.punctuation.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Whitespace:</span>
                    <span className="font-semibold">{detailedStats.whitespace.toLocaleString()}</span>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="font-semibold text-gray-700 border-b pb-2">Advanced Stats</h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Uppercase:</span>
                    <span className="font-semibold">{detailedStats.uppercase.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Lowercase:</span>
                    <span className="font-semibold">{detailedStats.lowercase.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Unique Characters:</span>
                    <span className="font-semibold">{detailedStats.uniqueChars.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Vowels:</span>
                    <span className="font-semibold">{detailedStats.vowels.toLocaleString()}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Reading Statistics */}
            <div className="mt-6 p-4 bg-gray-50 rounded-xl">
              <h3 className="font-semibold text-gray-700 mb-3">Reading Statistics</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <div>
                  <span className="text-gray-600">Average words per sentence:</span>
                  <div className="font-semibold text-lg">
                    {detailedStats.sentences > 0 ? (detailedStats.words / detailedStats.sentences).toFixed(1) : '0'}
                  </div>
                </div>
                <div>
                  <span className="text-gray-600">Average characters per word:</span>
                  <div className="font-semibold text-lg">
                    {detailedStats.words > 0 ? (detailedStats.charactersNoSpaces / detailedStats.words).toFixed(1) : '0'}
                  </div>
                </div>
                <div>
                  <span className="text-gray-600">Estimated reading time:</span>
                  <div className="font-semibold text-lg">
                    {Math.ceil(detailedStats.words / 200)} min
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StringLengthCounter;